import React from "react";
import { Check, Crown, Gem, Package, Rocket, Zap } from "lucide-react";

/** Gleiche Normalisierung wie in App.jsx (Select / Header). */
export const normalizePlanId = (raw) => {
  const s = (raw ?? "").toString().trim().toLowerCase();
  if (!s) return "free";
  if (s === "dev" || s.includes("dev") || s.includes("admin")) return "dev";
  if (s.includes("ultimate")) return "ultimate";
  if (s.includes("elite")) return "elite";
  if (s.includes("pro")) return "pro";
  if (s.includes("standard")) return "standard";
  if (s.includes("free")) return "free";
  return s;
};

/** Groß-/Kleinschreibung wie in App.jsx. */
export const formatPlanDisplay = (planId) => {
  if (!planId) return "";
  const normalized = normalizePlanId(planId);
  const displayMap = {
    free: "Free",
    standard: "Standard",
    pro: "Pro",
    elite: "Elite",
    ultimate: "Ultimate",
    dev: "Dev",
  };
  return (
    displayMap[normalized] ||
    (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : "")
  );
};

/**
 * Plan-Badge wie in App.jsx (Plan-Dropdown / Header): Lucide-Icons + Gradients.
 */
export function PlanBadge({ planId, size = "sm", variant = "hero", className = "", title }) {
  const id = normalizePlanId(planId);

  const styles = {
    free: {
      Icon: Check,
      bg: "from-emerald-500 via-green-600 to-lime-500",
      ring: "ring-emerald-200/70",
      glow: "from-emerald-200/60 via-lime-200/50 to-green-300/60",
    },
    standard: {
      Icon: Package,
      bg: "from-slate-500 via-slate-600 to-sky-600",
      ring: "ring-slate-200/60",
      glow: "from-slate-300/40 via-sky-300/40 to-blue-400/40",
    },
    pro: {
      Icon: Zap,
      bg: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
      ring: "ring-indigo-200/60",
      glow: "from-[#1E3A8A] via-[#4338CA] to-[#7266FF]",
    },
    elite: {
      Icon: Gem,
      bg: "from-amber-400 via-yellow-500 to-orange-500",
      ring: "ring-amber-200/70",
      glow: "from-amber-200/60 via-yellow-200/55 to-orange-200/55",
    },
    ultimate: {
      Icon: Rocket,
      bg: "from-red-500 via-rose-600 to-orange-500",
      ring: "ring-rose-200/60",
      glow: "from-red-300/55 via-rose-300/50 to-orange-300/50",
    },
    dev: {
      Icon: Crown,
      bg: "from-blue-600 via-indigo-600 to-violet-700",
      ring: "ring-blue-200/60",
      glow: "from-blue-300/50 via-indigo-300/45 to-violet-400/50",
    },
  };

  const cfg = styles[id] || styles.free;

  const sizes = {
    xxs: { wrap: "w-4 h-4 rounded-md", icon: "w-2.5 h-2.5", sparkle: false },
    xs: { wrap: "w-5 h-5 rounded-lg", icon: "w-3 h-3", sparkle: false },
    sm: {
      wrap: "w-8 h-8 rounded-xl",
      icon: "w-4 h-4",
      sparkle: true,
      sparklePos: "top-1.5 right-1.5",
      sparkleClass: "w-2 h-2",
    },
    lg: {
      wrap: "w-16 h-16 rounded-2xl",
      icon: "w-8 h-8",
      sparkle: true,
      sparklePos: "top-2 right-2",
      sparkleClass: "w-2.5 h-2.5",
    },
  };

  const s = sizes[size] || sizes.sm;
  const Icon = cfg.Icon;
  const compact = variant === "compact";

  return (
    <div
      title={title}
      className={[
        "relative inline-flex items-center justify-center overflow-hidden group",
        s.wrap,
        "bg-gradient-to-br",
        cfg.bg,
        compact ? "border border-white/20 shadow-sm" : `ring-1 ${cfg.ring} shadow-lg`,
        "transform-gpu transition-all duration-300",
        compact ? "" : "hover:shadow-xl hover:scale-[1.03]",
        className,
      ].join(" ")}
    >
      {!compact && (
        <div className={`absolute inset-0 bg-gradient-to-br ${cfg.glow} opacity-40 blur-lg`} />
      )}
      <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-white/35 to-transparent opacity-40" />
      <div className="absolute inset-0 rounded-[inherit] shadow-[inset_0_1px_0_rgba(255,255,255,0.35),inset_0_-1px_0_rgba(0,0,0,0.25)]" />
      {!compact && (
        <div className="absolute -left-1/2 top-0 h-full w-1/2 rotate-12 bg-white/20 blur-md opacity-0 translate-x-0 group-hover:opacity-100 group-hover:translate-x-[220%] transition-all duration-700" />
      )}
      <Icon className={`${s.icon} text-white drop-shadow-sm relative z-10`} />
    </div>
  );
}
